import { useEditContext } from '@components/utils/contexts/EditContext';
import Icon from '@components/utils/icons/icon';
import { ROUTE_API_PREVIEW_CLEAR, ROUTE_API_REVALIDATION } from '@constants';
import {
    REVALIDATION_HEADER,
    REVALIDATION_SECRET,
} from '@constants/revalidation';
import styled from '@emotion/styled';
import { colors } from '@lib/uikit/theme/colors';
import { getFullPath } from '@utils/link';
import { useRouter } from 'next/router';
import { useState } from 'react';

const Button = styled.button`
    position: fixed;
    bottom: 20px;
    right: 20px;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: ${colors.black};
    opacity: 0.9;
    cursor: pointer;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

    &:hover {
        opacity: 1;
    }
`;

const MenuContainer = styled.div<{ isOpen: boolean }>`
    position: fixed;
    bottom: 80px;
    right: 20px;
    background-color: ${(props) =>
        props.theme.palette.mode === 'light' ? colors.black : colors.white};
    color: ${(props) =>
        props.theme.palette.mode === 'light' ? colors.white : colors.black};
    border-radius: 8px;
    padding: ${({ theme }) => theme.spacing.sm};
    opacity: 0.7;
    display: ${(props) => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    gap: 8px;
    z-index: 99999;
`;

const MenuTitle = styled.p`
    font: ${(props) => props.theme.fonts.caption};
    font-weight: 600;
    color: ${colors.white};
    margin: 0 auto;
`;

const MenuDescription = styled.p`
    font: ${(props) => props.theme.fonts.caption};
    color: ${colors.white};
    max-width: 20em;
    margin: 0;
    padding: 0 16px;
    opacity: 0.8;
`;

const MenuItem = styled.button`
    background: transparent;
    color: inherit;
    font: ${(props) => props.theme.fonts.caption};
    cursor: pointer;
    padding: 8px 16px;
    border: none;
    width: 100%;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
        opacity: 0.8;
    }
`;

interface Props {
    isPreview?: boolean;
}

export const PreviewActionsButton = ({ isPreview }: Props) => {
    const router = useRouter();
    const { isEditMode } = useEditContext();
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    if (!isPreview || isEditMode) return null;

    const handleExit = async () => {
        try {
            const response = await fetch(ROUTE_API_PREVIEW_CLEAR);

            if (response.ok) {
                router.reload();
            }
        } catch (e) {
            console.log(e);
        }
    };

    const handleRevalidate = async () => {
        try {
            await fetch(ROUTE_API_REVALIDATION, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    [REVALIDATION_HEADER]: REVALIDATION_SECRET,
                },
                body: JSON.stringify({
                    url: getFullPath(router),
                }),
            });
        } catch (err) {
            console.error('[Preview] : Error revalidating page', err);
        } finally {
            setIsMenuOpen(false);
        }
    };

    return (
        <>
            <Button onClick={() => setIsMenuOpen(!isMenuOpen)}>
                <Icon name='menu' size='medium' color={colors.white} />
            </Button>
            <MenuContainer isOpen={isMenuOpen}>
                <MenuTitle>Preview mode for admin</MenuTitle>
                <MenuDescription>
                    You are currently viewing the most recent version of this
                    page.
                </MenuDescription>
                <MenuItem onClick={handleRevalidate}>
                    <Icon name='refresh' size='small' color={colors.white} />
                    Publish changes
                </MenuItem>
                <MenuItem onClick={handleExit}>
                    <Icon
                        name='exit_to_app'
                        size='small'
                        color={colors.white}
                    />
                    View Live Version
                </MenuItem>
            </MenuContainer>
        </>
    );
};
